import React, { useEffect, useState } from 'react';
import GraphCard, { generateTitle } from './GraphCard';

const API_BASE = process.env.REACT_APP_API_URL;

const createGraph = (fieldMeta) => {
  const firstField = fieldMeta.production?.[0]?.name || '';
  return {
    id: Date.now() + Math.random(),
    collection: 'production',
    xAxis: firstField,
    yAxis: firstField,
    aggregation: 'count',
    filters: [],
    dateField: '',
    startDate: '',
    endDate: '',
    graphType: 'bar',
    sortBy: 'dimension',
    sortDirection: 'asc',
    limit: 25,
    title: generateTitle({ xAxis: firstField, yAxis: firstField, aggregation: 'count' }),
    customTitle: false,
  };
};

export default function SwitchViewGrid({ isAdmin, userId }) {
  const [graphs, setGraphs] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [fieldOptions, setFieldOptions] = useState({ rma: [], production: [] });
  const [fieldMeta, setFieldMeta] = useState({ rma: [], production: [] });

  useEffect(() => {
    async function fetchFields() {
      try {
        const res = await fetch(`${API_BASE}/analytics/fields`);
        const data = await res.json();
        setFieldOptions({
          rma: Array.isArray(data.rma) ? data.rma : [],
          production: Array.isArray(data.production) ? data.production : [],
        });
        setFieldMeta({
          rma: Array.isArray(data?.meta?.rma) ? data.meta.rma : (Array.isArray(data.rma) ? data.rma.map(name => ({ name, type: 'String' })) : []),
          production: Array.isArray(data?.meta?.production) ? data.meta.production : (Array.isArray(data.production) ? data.production.map(name => ({ name, type: 'String' })) : []),
        });
      } catch (e) {
        console.error('Failed to load field options', e);
      }
    }
    fetchFields();
  }, []);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        let viewRes = await fetch(`${API_BASE}/analytics/views?userId=${userId}`);
        let view = await viewRes.json();
        if (!view || !view.graphs || view.graphs.length === 0) {
          viewRes = await fetch(`${API_BASE}/analytics/views?type=global`);
          view = await viewRes.json();
        }
        if (view && view.graphs) {
          setGraphs(
            view.graphs.map(g => ({
              id: g.id || Date.now() + Math.random(),
              collection: g.collection || 'production',
              xAxis: g.xAxis || '',
              yAxis: g.yAxis || '',
              aggregation: g.aggregation || 'count',
              filters: g.filters || [],
              dateField: g.dateField || '',
              startDate: g.startDate ? g.startDate.slice(0, 10) : '',
              endDate: g.endDate ? g.endDate.slice(0, 10) : '',
              graphType: g.graphType || 'bar',
              sortBy: g.sortBy || 'dimension',
              sortDirection: g.sortDirection || 'asc',
              limit: g.limit || 25,
              title:
                g.title ||
                generateTitle({
                  xAxis: g.xAxis || '',
                  yAxis: g.yAxis || '',
                  aggregation: g.aggregation || 'count',
                }),
              customTitle: g.customTitle || false,
            }))
          );
        }
      } catch (e) {
        console.error('Failed to load view', e);
      }
    })();
  }, [userId]);

  const addGraph = () => {
    setGraphs(prev => [...prev, createGraph(fieldMeta)]);
  };

  const removeGraph = id => {
    setGraphs(prev => prev.filter(g => g.id !== id));
  };

  const updateGraph = (id, updates) => {
    setGraphs(prev => prev.map(g => (g.id === id ? { ...g, ...updates } : g)));
  };

  const saveView = async type => {
    try {
      await fetch(`${API_BASE}/analytics/views`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          type: type === 'global' ? 'global' : 'personal',
          graphs,
        }),
      });
    } catch (e) {
      console.error('Failed to save view', e);
    }
  };

  return (
    <div className="ni-stack">
      <div className="ni-panel ni-panel--dense">
        <div className="ni-section-heading">
          <div>
            <h2 className="ni-section-title">Analytics Explorer</h2>
            <p className="ni-section-copy">
              Build dynamic views for production and RMA data. Use dimensions, metrics, operator-based filters, sorting, and limits instead of adding a new static card every time.
            </p>
          </div>
          <div className="ni-toolbar">
            {editMode && (
              <button
                onClick={addGraph}
                disabled={fieldMeta.production.length === 0}
                className="ni-button ni-button--primary"
              >
                Add Graph
              </button>
            )}
            <button
              onClick={() => saveView('user')}
              className="ni-button ni-button--ghost"
            >
              Save Personal View
            </button>
            {isAdmin && (
              <button
                onClick={() => saveView('global')}
                className="ni-button ni-button--muted"
              >
                Save Global View
              </button>
            )}
            <button
              onClick={() => setEditMode(m => !m)}
              className="ni-button ni-button--primary"
            >
              {editMode ? 'Done' : 'Edit Mode'}
            </button>
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: '1rem',
        }}
      >
        {graphs.map(g => (
          <GraphCard
            key={g.id}
            graph={g}
            editable={editMode}
            onRemove={removeGraph}
            onChange={updateGraph}
            fieldOptions={fieldOptions}
            fieldMeta={fieldMeta}
          />
        ))}
      </div>
    </div>
  );
}
